import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import Box from 'carbon-react/lib/components/box';
import Textbox from 'carbon-react/lib/components/textbox';
import Textarea from 'carbon-react/lib/components/textarea';
import { MultiSelect, Option } from 'carbon-react/lib/components/select';

import {
  FooterMode,
  WizardScreen
} from '@futrli/ui-components/components/sidebar/wizard/screens/wizard-screen/wizard-screen';
import { WizardFormError } from '@futrli/ui-components/components/sidebar/wizard/wizard-form-error/wizard-form-error';
import { WizardFormValidationErrorSummary } from '@futrli/ui-components/components/sidebar/wizard/wizard-form-validation-error-summary/wizard-form-validation-error-summary';
import { WizardRequiredFieldInfo } from '@futrli/ui-components/components/sidebar/wizard/wizard-required-field-info/wizard-required-field-info';

import { WorkspaceMember } from '@futrli-sdk/people';

interface EmailFormScreenProps {
  reportName: string;
  members: WorkspaceMember[];
  selectedUsers: WorkspaceMember[];
  setSelectedUsers: (users: WorkspaceMember[]) => void;
  emailSubject: string;
  setEmailSubject: (value: string) => void;
  emailBody: string;
  setEmailBody: (value: string) => void;
  validationErrors: Record<string, { id: string; message: string }>;
  revalidate: () => void;
  fieldIds: { SEND_TO: string; SUBJECT: string; BODY: string };
  error?: string;
  onCancel: () => void;
  onSend: () => void;
}

export const EmailFormScreen = ({
  reportName,
  members,
  selectedUsers,
  setSelectedUsers,
  emailSubject,
  setEmailSubject,
  emailBody,
  setEmailBody,
  validationErrors,
  revalidate,
  fieldIds,
  error,
  onCancel,
  onSend
}: EmailFormScreenProps) => {
  const intl = useIntl();

  const memberOptions = useMemo(
    () =>
      (members || []).map(m => ({
        ...m,
        text: `${m.name.first_name} ${m.name.last_name} <${m.email_address}>`
      })),
    [members]
  );

  const handleMultiSelectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValues = ((event.target as unknown) as { value: WorkspaceMember[] }).value;
    setSelectedUsers(selectedValues);
    revalidate();
  };

  return (
    <WizardScreen
      footerMode={FooterMode.CREATE}
      onCancel={onCancel}
      onCreate={onSend}
      createText={intl.formatMessage({ id: 'predict.send', defaultMessage: 'Send' })}
    >
      <>
        {error && <WizardFormError message={error} mb={3} />}

        <WizardFormValidationErrorSummary errors={validationErrors} mb={3} />

        <WizardRequiredFieldInfo />

        <Textbox
          label={intl.formatMessage({ id: 'reporting.report', defaultMessage: 'Report' })}
          value={reportName}
          onChange={() => undefined}
          disabled
          required
        />

        <Box mb={3} mt={3} backgroundColor="#CCD6DB" height="1px" />

        <MultiSelect
          id={fieldIds.SEND_TO}
          name={fieldIds.SEND_TO}
          label={intl.formatMessage({ id: 'predict.send_to', defaultMessage: 'Send to' })}
          inputHint={intl.formatMessage({
            id: 'predict.email_pdf_send_to_hint',
            defaultMessage: 'Select one or more. Type to search if needed.'
          })}
          value={(selectedUsers as unknown) as string[]}
          onChange={handleMultiSelectChange}
          onBlur={revalidate}
          error={validationErrors[fieldIds.SEND_TO]?.message}
          mb={2}
          required
        >
          {memberOptions.map(member => (
            <Option key={member.id} text={member.text} value={(member as unknown) as string} />
          ))}
        </MultiSelect>

        <Textbox
          label={intl.formatMessage({ id: 'predict.email_pdf_subject_label', defaultMessage: 'Email subject' })}
          name={fieldIds.SUBJECT}
          id={fieldIds.SUBJECT}
          value={emailSubject}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmailSubject(e.target.value)}
          onBlur={revalidate}
          error={validationErrors[fieldIds.SUBJECT]?.message}
          required
          mb={2}
        />

        <Textarea
          id={fieldIds.BODY}
          label={intl.formatMessage({ id: 'predict.email_pdf_body_label', defaultMessage: 'Email body' })}
          value={emailBody}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEmailBody(e.target.value)}
          onBlur={revalidate}
          required
          rows={8}
          error={validationErrors[fieldIds.BODY]?.message}
          expandable
        />
      </>
    </WizardScreen>
  );
};
